import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import Title from '@/components/ui/Title';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useStripeOnMount } from '@/provider/stripeLoader';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { fetchPaymentMethods } from '@/features/billing/billingThunks';
import { readPaymentMethodId, saveDefaultCard } from '@/features/billing/subscriptionCheckout';
import { cn } from '@/lib/utils';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

function StripeInputWithLabel({
  label,
  id,
  StripeElement,
  onCompleteChange,
}: {
  label: string;
  id: string;
  StripeElement: typeof CardNumberElement | typeof CardExpiryElement | typeof CardCvcElement;
  onCompleteChange?: (complete: boolean) => void;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  const isActive = isFocused || hasValue;

  return (
    <div className={cn('relative px-4 py-2 h-14 rounded-lg border border-input bg-white')}>
      <label
        className={cn(
          'absolute transition-all duration-200 pointer-events-none text-tertiary',
          isActive ? 'text-sm top-0.5 left-3 px-1' : 'text-base top-1/2 left-4 -translate-y-1/2',
        )}
        htmlFor={id}
      >
        {label}
      </label>
      <div className="pt-4">
        <StripeElement
          id={id}
          options={{
            placeholder: '',
            style: {
              base: {
                color: '#09090a',
                fontSize: '16px',
                fontWeight: '400',
                padding: '8px 8px 8px 8px',
              },
            },
          }}
          onChange={(event) => {
            setHasValue(!event.empty);
            onCompleteChange?.(event.complete);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </div>
    </div>
  );
}

function CardSetupComponent({
  onSuccess,
  onCancel,
}: {
  onSuccess?: () => void;
  onCancel?: () => void;
}) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('settings');
  const stripe = useStripe();
  const elements = useElements();
  useStripeOnMount();
  const workspaceId = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.id);
  const user = useAppSelector((s: RootState) => s.auth.user);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isCardNumberComplete, setIsCardNumberComplete] = useState(false);
  const [isCardExpiryComplete, setIsCardExpiryComplete] = useState(false);
  const [isCardCvcComplete, setIsCardCvcComplete] = useState(false);

  const isFormValid = isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete;

  const handleAddCard = async () => {
    if (!stripe || !elements) {
      toast.error(t('payment.toast.stripeNotReady'));
      return;
    }
    if (!workspaceId) {
      toast.error(t('payment.toast.workspaceNotSelected'));
      return;
    }

    setIsProcessing(true);
    try {
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        toast.error(t('payment.toast.cardElementNotFound'));
        return;
      }

      const card = await saveDefaultCard({
        dispatch,
        workspaceId,
        confirmCardSetup: async (clientSecret) => {
          const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || undefined,
                email: user?.email ?? undefined,
              },
            },
          });
          return {
            error: error ? { message: error.message } : null,
            paymentMethodId: readPaymentMethodId(setupIntent?.payment_method),
          };
        },
      });

      if (card.kind !== 'saved') {
        toast.error(
          (card.kind === 'refused' ? card.message : null) ?? t('payment.toast.addCardError'),
        );
        return;
      }

      await dispatch(fetchPaymentMethods(workspaceId));
      toast.success(t('payment.toast.cardAdded'));
      onSuccess?.();
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message;
      toast.error(message ?? t('payment.toast.addCardError'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <StripeInputWithLabel
        label={t('payment.cardNumber')}
        id="cardNumber"
        StripeElement={CardNumberElement}
        onCompleteChange={setIsCardNumberComplete}
      />

      <div className="flex gap-4">
        <div className="w-1/2">
          <StripeInputWithLabel
            label={t('payment.expiryDate')}
            id="cardExpiry"
            StripeElement={CardExpiryElement}
            onCompleteChange={setIsCardExpiryComplete}
          />
        </div>
        <div className="w-1/2">
          <StripeInputWithLabel
            label={t('payment.cvv')}
            id="cardCvc"
            StripeElement={CardCvcElement}
            onCompleteChange={setIsCardCvcComplete}
          />
        </div>
      </div>

      <div className="flex gap-4 pt-12">
        <Button
          variant="outline"
          disabled={isProcessing}
          className="w-1/2"
          type="button"
          onClick={() => onCancel?.()}
        >
          {t('payment.cancel')}
        </Button>
        <Button
          disabled={!stripe || isProcessing || !isFormValid}
          className="w-1/2"
          onClick={handleAddCard}
        >
          {isProcessing && <Loader />}
          {isProcessing ? t('payment.processing') : t('payment.add')}
        </Button>
      </div>
    </div>
  );
}

export default function AddCard() {
  const [open, setOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const { t } = useTranslation('settings');

  const handleCancel = () => {
    setOpen(false);
    setResetKey((k) => k + 1);
  };

  return (
    <div className="flex items-center justify-between">
      <div>
        <Title level={2}>{t('payment.title')}</Title>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="px-6">
            <Plus className="size-4" /> {t('payment.addAnotherCard')}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-188 p-6 rounded-[20px]">
          <DialogHeader className="hidden">
            <DialogTitle>{t('payment.addAnotherCard')}</DialogTitle>
          </DialogHeader>

          <div className="pb-8">
            <Title level={1}>{t('payment.addCardTitle')}</Title>
          </div>

          <CardSetupComponent
            key={resetKey}
            onSuccess={() => setOpen(false)}
            onCancel={handleCancel}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
